import type { Dispatch, SetStateAction } from 'react';
import {
  TaskPhase,
  type TaskCancelledEvent,
  type TaskCompletedEvent,
  type TaskErrorEvent,
  type TaskProgressEvent,
  type TaskQuestionsReadyEvent,
  type TaskReviewReadyEvent,
  type TaskStatus,
  type TaskVerificationCompleteEvent,
  type TaskWorkflowState,
  normalizeQuestions,
} from './types';

export interface TaskSocketHandlerDeps {
  taskId: string;
  setTaskStatus: Dispatch<SetStateAction<TaskStatus | null>>;
  setError: Dispatch<SetStateAction<string | null>>;
  fetchDiff: (taskIdOverride?: string) => void | Promise<void>;
}

export interface TaskSocketHandlers {
  'task-progress': (data: TaskProgressEvent) => void;
  'task-questions-ready': (
    data: TaskQuestionsReadyEvent & { workflow?: TaskWorkflowState },
  ) => void;
  'task-verification-complete': (
    data: TaskVerificationCompleteEvent & { workflow?: TaskWorkflowState },
  ) => void;
  'task-review-ready': (data: TaskReviewReadyEvent & { workflow?: TaskWorkflowState }) => void;
  'task-completed': (data: TaskCompletedEvent) => void;
  'task-error': (data: TaskErrorEvent) => void;
  'task-cancelled': (data: TaskCancelledEvent) => void;
}

export function createTaskSocketHandlers(deps: TaskSocketHandlerDeps): TaskSocketHandlers {
  const { taskId, setTaskStatus, setError, fetchDiff } = deps;

  return {
    'task-progress': (data) => {
      if (data.taskId !== taskId) return;
      setTaskStatus((prev) => {
        if (!prev) return null;
        const phaseStartedAt = data.timestamp || data.phaseStartedAt || prev.phaseStartedAt;
        return {
          ...prev,
          ...data,
          phase: data.currentPhase || prev.phase,
          phaseStartedAt,
        };
      });
    },

    'task-questions-ready': (data) => {
      if (data.taskId !== taskId) return;
      setTaskStatus((prev) =>
        prev
          ? {
              ...prev,
              phase: TaskPhase.WAITING_FOR_INPUT,
              questions: normalizeQuestions(data.questions),
              workflow: data.workflow || prev.workflow,
            }
          : null,
      );
    },

    'task-verification-complete': (data) => {
      if (data.taskId !== taskId) return;
      setTaskStatus((prev) =>
        prev
          ? {
              ...prev,
              phase: TaskPhase.WAITING_FOR_REVIEW,
              verification: data.verification,
              workflow: data.workflow || prev.workflow,
            }
          : null,
      );
    },

    'task-review-ready': (data) => {
      if (data.taskId !== taskId) return;
      setTaskStatus((prev) => {
        if (!prev) return null;
        const updates: Partial<TaskStatus> = {
          ...prev,
          phase: TaskPhase.WAITING_FOR_REVIEW,
          workflow: data.workflow || prev.workflow,
        };
        if (data.verification) {
          updates.verification = data.verification;
        }
        if (data.diff_key && prev.preview) {
          updates.preview = { ...prev.preview, diffKey: data.diff_key };
        }
        return updates as TaskStatus;
      });
      fetchDiff();
    },

    'task-completed': (data) => {
      if (data.taskId !== taskId) return;
      setTaskStatus((prev) =>
        prev
          ? {
              ...prev,
              status: 'completed',
              phase: TaskPhase.CREATE_PR,
              workflow: data.workflow || prev.workflow,
              outputs: {
                ...prev.outputs,
                pr_url: data.pr_url,
                success: true,
              },
            }
          : null,
      );
    },

    'task-error': (data) => {
      if (data.taskId !== taskId) return;
      const errorMsg = data.message || data.error || 'Unknown error';
      setError(errorMsg);
      setTaskStatus((prev) =>
        prev
          ? {
              ...prev,
              status: 'failed',
              workflow: data.workflow || prev.workflow,
              errors: [...(prev.errors || []), errorMsg],
            }
          : null,
      );
    },

    'task-cancelled': (data) => {
      if (data.taskId !== taskId) return;
      setTaskStatus((prev) =>
        prev
          ? {
              ...prev,
              status: 'cancelled',
              workflow: data.workflow || prev.workflow,
            }
          : null,
      );
    },
  };
}
